<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Semester Tests</h1>
            <p class="text-blue-100">Keep track of your upcoming tests</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Back button -->
      <div class="mb-4">
        <router-link to="/timetable" class="inline-flex items-center text-blue-900 hover:text-blue-700">
          <span class="material-symbols-outlined mr-1">arrow_back</span>
          Back to Timetables
        </router-link>
      </div>

      <!-- Search and Filter Bar -->
      <div class="bg-white p-4 rounded-lg shadow-sm mb-4">
        <div class="flex flex-col md:flex-row gap-4">
          <div class="flex-1">
            <div class="relative">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                <span class="material-symbols-outlined">search</span>
              </span>
              <input
                type="text"
                v-model="searchQuery"
                placeholder="Search by module code, venue..."
                class="pl-10 pr-4 py-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div class="flex gap-2">
            <!-- View Toggle -->
            <div class="inline-flex rounded-md shadow-sm" role="group">
              <button
                @click="viewMode = 'list'"
                :class="[
                  'px-4 py-2 text-sm font-medium border border-blue-900',
                  viewMode === 'list' 
                    ? 'bg-blue-900 text-white' 
                    : 'bg-white text-blue-900 hover:bg-blue-50'
                ]"
                class="rounded-l-lg"
              >
                <span class="flex items-center">
                  <span class="material-symbols-outlined mr-1">list</span>
                  List
                </span>
              </button>
              <button
                @click="viewMode = 'calendar'"
                :class="[
                  'px-4 py-2 text-sm font-medium border border-blue-900 border-l-0',
                  viewMode === 'calendar' 
                    ? 'bg-blue-900 text-white' 
                    : 'bg-white text-blue-900 hover:bg-blue-50'
                ]"
                class="rounded-r-lg"
              >
                <span class="flex items-center">
                  <span class="material-symbols-outlined mr-1">calendar_month</span>
                  Calendar
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center py-8">
        <div class="w-8 h-8 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
      </div>

      <!-- Empty State -->
      <div 
        v-else-if="filteredSemesterTests.length === 0" 
        class="text-center py-8 bg-white rounded-lg shadow p-6"
      >
        <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">assignment</span>
        <h3 class="text-lg font-medium text-gray-700 mb-2">No semester tests found</h3>
        <p v-if="userModules.length === 0" class="text-gray-500 max-w-md mx-auto">
          You haven't added any modules yet. 
          <router-link to="/update-university" class="text-blue-600 hover:text-blue-800 underline">
            Add modules to your profile
          </router-link> 
          to see your semester tests.
        </p>
        <p v-else-if="searchQuery" class="text-gray-500 max-w-md mx-auto">
          Try adjusting your search criteria.
        </p>
        <p v-else class="text-gray-500 max-w-md mx-auto">
          No semester tests were found for your modules.
        </p>
      </div>

      <!-- List View -->
      <div v-else-if="viewMode === 'list'" class="bg-white rounded-lg shadow p-6">
        <!-- Next Test Smart Card -->
        <div v-if="nextTest" class="bg-blue-50 border-l-4 border-blue-700 rounded-lg p-4 mb-6 flex items-center gap-4 shadow-sm">
          <span class="material-symbols-outlined text-3xl text-blue-700">event_upcoming</span>
          <div>
            <div class="font-semibold text-blue-900 text-base mb-1">
              Next test: {{ nextTest.moduleCode }} 
            </div>
            <div class="text-sm text-blue-800">
              {{ nextTest.day }}, {{ nextTest.date }}, {{ nextTest.time }} at {{ nextTest.venue }}
            </div>
            <div class="mt-1">
              <span
                :class=" [
                  'inline-block px-3 py-1 rounded-full text-xs font-semibold',
                  nextTest.daysToTest <= 1
                    ? 'bg-red-500 text-white animate-pulse'
                    : nextTest.daysToTest <= 7
                      ? 'bg-yellow-400 text-gray-900'
                      : 'bg-green-500 text-white'
                ]"
              >
                {{ nextTest.daysToTest === 0 ? 'Today' : nextTest.daysToTest === 1 ? 'Tomorrow' : `In ${nextTest.daysToTest} days` }}
              </span>
            </div>
          </div>
        </div>

        <!-- Tests by Module -->
        <div class="space-y-6">
          <div 
            v-for="(tests, moduleCode) in groupedSemesterTests" 
            :key="moduleCode"
            class="border-b border-gray-200 pb-6 last:border-0"
          >
            <div class="flex justify-between items-center mb-3">
              <h3 class="font-medium text-gray-800">{{ moduleCode }}</h3>
              <span 
                v-if="isUserModule(moduleCode)"
                class="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
              >
                My Module
              </span>
            </div>
            
            <div class="space-y-3">
              <div 
                v-for="test in tests" 
                :key="`${moduleCode}-${test.test}-${test.date}`"
                class="p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div class="flex items-center justify-between">
                  <div class="font-medium text-gray-900">
                    {{ test.test }}
                  </div>
                  <span 
                    :class=" [
                      'inline-block px-3 py-1 rounded-full text-xs font-semibold',
                      isTestUpcoming(test.date) ? 
                        getDaysDifference(test.date) <= 7 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    ]"
                  >
                    {{ formatDateStatus(test.date) }}
                  </span>
                </div>
                <div class="mt-2 text-sm text-gray-700 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-gray-500 mr-2">event</span>
                    {{ test.day }}, {{ formatDate(test.date) }}
                  </div>
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-gray-500 mr-2">schedule</span>
                    {{ test.time }}
                  </div>
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-gray-500 mr-2">location_on</span>
                    {{ test.campus }}, {{ test.venue }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Calendar View -->
      <div v-else-if="viewMode === 'calendar'" class="bg-white rounded-lg shadow p-6">
        <SemesterTestCalendar
          :tests="filteredSemesterTests"
        />
      </div>
    </div>
  </div>
  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
// Use the new calendar component
import SemesterTestCalendar from '@/components/timetable/SemesterTestCalendar.vue';
import { mapState, mapGetters } from 'vuex';
import { parseTestDate, getColorForTest } from '@/composables/useTimetables';

export default {
  name: 'SemesterTestsPage',
  components: {
    AppHeader,
    BottomNav,
    SemesterTestCalendar
  },
  data() {
    return {
      semesterTests: [],
      loading: true,
      viewMode: 'list',
      searchQuery: '',
      fetchInProgress: false
    };
  },
  computed: {
    ...mapState(['profile', 'semesterTestsLoading']),
    ...mapGetters(['semesterTestsForModules']),
    
    userModules() {
      // Get modules from user profile, return empty array if not available
      if (!this.profile || !this.profile.university || !this.profile.university.modules) {
        return [];
      }
      
      // Handle both array of strings and array of objects with code property
      return this.profile.university.modules.map(module => {
        if (typeof module === 'string') return module;
        if (module && module.code) return module.code;
        return '';
      }).filter(Boolean); // Remove empty values
    },
    
    filteredSemesterTests() {
      if (!this.semesterTests.length) return [];
      
      // Remove duplicates by creating a unique key for each test
      const uniqueTests = new Map();
      
      for (const test of this.semesterTests) {
        // Create a unique key based on module, test type, date, time, and venue
        const key = `${test.moduleCode}-${test.test}-${test.date}-${test.time}-${test.venue}`;
        
        // Only add if this exact test doesn't exist yet
        if (!uniqueTests.has(key)) {
          uniqueTests.set(key, test);
        }
      }
      
      // Convert back to array
      const uniqueTestsArray = Array.from(uniqueTests.values());
      
      // Apply search filter
      return uniqueTestsArray.filter(test => {
        // Filter by search query
        if (this.searchQuery) {
          const searchTerm = this.searchQuery.toLowerCase();
          return test.moduleCode.toLowerCase().includes(searchTerm) ||
                 (test.venue && test.venue.toLowerCase().includes(searchTerm)) ||
                 (test.campus && test.campus.toLowerCase().includes(searchTerm));
        }
        
        return true;
      });
    },
    
    groupedSemesterTests() {
      const grouped = {};
      
      for (const test of this.filteredSemesterTests) {
        if (!grouped[test.moduleCode]) {
          grouped[test.moduleCode] = [];
        }
        grouped[test.moduleCode].push(test);
      }
      
      // Sort each module's tests by date
      for (const moduleCode in grouped) {
        grouped[moduleCode].sort((a, b) => {
          const dateA = this.parseTestDate(a.date);
          const dateB = this.parseTestDate(b.date);
          return dateA - dateB;
        });
      }
      
      return grouped;
    },
    
    nextTest() {
      const now = new Date();
      
      // Filter tests that haven't happened yet from the unique filtered list
      const upcomingTests = this.filteredSemesterTests
        .filter(test => {
          const testDate = this.parseTestDate(test.date);
          return testDate >= now;
        })
        .map(test => {
          const testDate = this.parseTestDate(test.date);
          const daysToTest = Math.ceil((testDate - now) / (1000 * 60 * 60 * 24));
          return { ...test, daysToTest };
        });
      
      // Sort by date and return the earliest one
      if (upcomingTests.length > 0) {
        return upcomingTests.sort((a, b) => a.daysToTest - b.daysToTest)[0];
      }
      
      return null;
    },

    formattedTestsForCalendar() {
      return this.filteredSemesterTests.map(test => {
        const testDate = this.parseTestDate(test.date);
        // Extract start and end times
        const timeRange = test.time.split('-');
        const startTime = timeRange[0].trim();
        const endTime = timeRange.length > 1 ? timeRange[1].trim() : '';
        
        return {
          id: `${test.moduleCode}-${test.test}-${test.date}`,
          title: `${test.moduleCode} - ${test.test}`,
          date: testDate,
          start: startTime,
          end: endTime,
          location: test.venue,
          campus: test.campus,
          // Use color based on how close the test is
          color: this.getColorForTest(testDate)
        };
      });
    }
  },
  watch: {
    userModules: {
      handler(newModules, oldModules) {
        // Only reload if the module list has actually changed
        if (!this.arraysEqual(newModules, oldModules)) {
          this.fetchSemesterTests();
        }
      },
      deep: true
    }
  },
  async mounted() {
    await this.fetchSemesterTests();
  },
  methods: {
    async fetchSemesterTests() {
      if (this.fetchInProgress) return;
      
      try {
        this.loading = true;
        this.fetchInProgress = true;
        
        // Only fetch user's modules
        if (this.userModules && this.userModules.length > 0) {
          console.log(`Fetching semester tests for ${this.userModules.length} modules:`, this.userModules);
          
          // Use the store's action to fetch and cache semester tests
          const tests = await this.$store.dispatch('fetchSemesterTests', {
            moduleCodes: this.userModules
          });
          
          this.semesterTests = tests;
          console.log(`Loaded ${this.semesterTests.length} semester tests`);
        } else {
          console.log('No user modules found, returning empty data');
          this.semesterTests = [];
        }
      } catch (error) {
        console.error('Error fetching semester tests:', error);
        this.$store.dispatch('showAlert', {
          message: 'Failed to load semester tests',
          type: 'error'
        });
      } finally {
        this.loading = false;
        this.fetchInProgress = false;
      }
    },
    
    // Helper method to compare arrays
    arraysEqual(arr1, arr2) {
      if (!arr1 || !arr2) return false;
      if (arr1.length !== arr2.length) return false;
      
      // Sort and stringify for comparison
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      
      return JSON.stringify(sorted1) === JSON.stringify(sorted2);
    },
    
    isUserModule(moduleCode) {
      if (!this.userModules.length) return false;
      return this.userModules.some(userModule => {
        const userModuleCode = typeof userModule === 'string' ? userModule : (userModule?.code || '');
        return moduleCode.startsWith(userModuleCode);
      });
    },
    
    parseTestDate(dateStr) {
      return parseTestDate(dateStr);
    },
    
    formatDate(dateStr) {
      const date = this.parseTestDate(dateStr);
      return new Intl.DateTimeFormat('en-ZA', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric'
      }).format(date);
    },
    
    isTestUpcoming(dateStr) {
      const testDate = this.parseTestDate(dateStr);
      const now = new Date();
      return testDate >= now;
    },
    
    getDaysDifference(dateStr) {
      const testDate = this.parseTestDate(dateStr);
      const now = new Date();
      const diffTime = Math.abs(testDate - now);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    
    formatDateStatus(dateStr) {
      const testDate = this.parseTestDate(dateStr);
      const now = new Date();
      
      if (testDate < now) {
        return "Completed";
      }
      
      const diffTime = testDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Tomorrow";
      if (diffDays < 7) return `In ${diffDays} days`;
      if (diffDays < 30) return `In ${Math.floor(diffDays / 7)} weeks`;
      
      return this.formatDate(dateStr);
    },
    
    getColorForTest(testDate) {
      return getColorForTest(testDate);
    }
  }
};
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    width: 25%;
  }
  50% {
    width: 75%;
  }
}

.animate-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>